import { useMemo, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../@/components/ui/dialog";
import { Button } from "../../@/components/ui/button";
import { Input } from "../../@/components/ui/input";
import { FileIcon, Trash2 } from "lucide-react";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import { toast } from "sonner";
import { ClipLoader } from "react-spinners";

const SUPPORTED_EXTENSIONS = [".csv", ".xlsx", ".xls"];

const parseCsvFile = (file) =>
  new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      transformHeader: (header) => header.trim().toLowerCase(),
      complete: ({ data }) => resolve(data),
      error: reject,
    });
  });

const parseSpreadsheet = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = () => {
      const workbook = XLSX.read(new Uint8Array(reader.result), { type: "array" });
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      resolve(XLSX.utils.sheet_to_json(worksheet));
    };
    reader.readAsArrayBuffer(file);
  });

const parseFile = (file) =>
  file.name.toLowerCase().endsWith(".csv") ? parseCsvFile(file) : parseSpreadsheet(file);

const normaliseEmail = (row = {}) =>
  (row.email ??
    row.Email ??
    row["email address"] ??
    row["Email Address"] ??
    row["e-mail"] ??
    row.emails ??
    row.Emails ??
    row.EMAIL ??
    row.EMAILS ??
    "")
    .toString()
    .trim()
    .toLowerCase();

export default function AttendeesBulkDialog({
  open,
  onOpenChange,
  allUsers = [],
  allTms = [],
  selectedAttendees = [],
  onAttendeesSelected,
  templateUrl = "https://lms-internal.zinduaschool.com/templates/bulk-enroll-emails.csv",
}) {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const everyone = useMemo(() => [...allUsers, ...allTms], [allUsers, allTms]);

  const emailToId = useMemo(() => {
    const map = new Map();
    everyone.forEach((person) => {
      const email = person?.email?.trim().toLowerCase();
      if (email && !map.has(email)) {
        map.set(email, person.id);
      }
    });
    return map;
  }, [everyone]);

  const resetState = () => {
    setFiles([]);
    setUploading(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const addFiles = (incomingList) => {
    if (!incomingList?.length) return;
    setFiles((prev) => {
      const seen = new Set(prev.map((file) => `${file.name}-${file.size}`));
      const next = Array.from(incomingList).filter((file) => {
        const extension = file.name.slice(file.name.lastIndexOf(".")).toLowerCase();
        if (!SUPPORTED_EXTENSIONS.includes(extension)) {
          toast.error(`Unsupported file type: ${extension}. Please upload CSV or spreadsheet files.`);
          return false;
        }
        const key = `${file.name}-${file.size}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
      return [...prev, ...next];
    });
  };

  const onFileChange = (event) => {
    addFiles(event.target.files);
    event.target.value = "";
  };

  const onDrop = (event) => {
    event.preventDefault();
    addFiles(event.dataTransfer.files);
  };

  const removeFile = (name, size) =>
    setFiles((prev) => prev.filter((file) => file.name !== name || file.size !== size));

  const handleUpload = async () => {
    if (!files.length) {
      toast.error("Add at least one file to continue.");
      return;
    }
    setUploading(true);
    try {
      const parsedRows = (await Promise.all(files.map((file) => parseFile(file)))).flat();
      const emails = parsedRows.map(normaliseEmail).filter(Boolean);

      if (!emails.length) {
        toast.error("No valid emails detected in the uploaded files.");
        setUploading(false);
        return;
      }

      const foundIds = [];
      const missingEmails = new Set();

      emails.forEach((email) => {
        const id = emailToId.get(email);
        if (id) {
          foundIds.push(id);
        } else {
          missingEmails.add(email);
        }
      });

      if (!foundIds.length) {
        toast.error("None of the uploaded emails matched existing users.");
        setUploading(false);
        return;
      }

      const nextSelection = Array.from(new Set([...selectedAttendees, ...foundIds]));
      onAttendeesSelected?.(nextSelection);

      let message = `${foundIds.length} attendee${foundIds.length === 1 ? "" : "s"} added from uploads.`;
      if (missingEmails.size) {
        message += ` ${missingEmails.size} email${missingEmails.size === 1 ? " was" : "s were"} skipped because they were not found.`;
      }
      toast.success(message);

      resetState();
      onOpenChange?.(false);
    } catch (error) {
      console.error("Failed to process attendee uploads", error);
      toast.error("Failed to process the uploaded files. Try again.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(value) => {
        onOpenChange?.(value);
        if (!value) {
          resetState();
        }
      }}
    >
      <DialogContent className="max-w-lg w-[95vw] bg-(--surface-base) text-(--neutral-900)">
        <DialogHeader>
          <DialogTitle className="text-2xl font-heading">Bulk Select Attendees</DialogTitle>
          <DialogDescription className="text-sm text-(--neutral-600)">
            Upload CSV file of attendees to select.{" "}
            <a
              href={templateUrl}
              download
              target="_blank"
              rel="noopener noreferrer"
              className="text-(--color-primary) underline"
            >
              Click to download template
            </a>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div
            onDragOver={(event) => event.preventDefault()}
            onDrop={onDrop}
            className="flex flex-col items-center justify-center gap-3 border border-dashed border-(--neutral-300) rounded-lg py-10 px-4 text-center bg-(--surface-base) text-(--neutral-600)"
          >
            <FileIcon className="w-10 h-10 text-(--neutral-400)" />
            <p className="font-heading text-(--neutral-800)">
              Drag and drop or{" "}
              <button
                type="button"
                className="text-(--color-primary) underline ml-1"
                onClick={() => fileInputRef.current?.click()}
              >
                Choose file
              </button>{" "}
              to upload
            </p>
            <Input
              ref={fileInputRef}
              type="file"
              accept=".csv,.xlsx,.xls"
              className="hidden"
              multiple
              onChange={onFileChange}
            />
          </div>

          <div className="flex justify-between">
            <p className="text-xs text-(--neutral-500)">Accepted file types: CSV, XLS, XLSX</p>
            <p className="text-xs text-(--neutral-500)">Max file size: 10MB</p>
          </div>

          {files.length > 0 && (
            <div className="space-y-2 max-h-40 overflow-y-auto bg-(--neutral-50)">
              {files.map((file) => (
                <div
                  key={`${file.name}-${file.size}`}
                  className="flex items-center justify-between border border-(--neutral-200) rounded-md px-3 py-2 bg-(--neutral-100)"
                >
                  <div className="flex items-center gap-3">
                    <FileIcon className="w-5 h-5 text-(--color-primary)" />
                    <div>
                      <p className="text-sm font-medium">{file.name}</p>
                      <p className="text-xs text-(--neutral-500)">
                        {(file.size / (1024 * 1024)).toFixed(2)} MB
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="text-(--neutral-500) hover:text-(--color-destructive)"
                    onClick={() => removeFile(file.name, file.size)}
                    aria-label="Remove file"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              onOpenChange?.(false);
              resetState();
            }}
            disabled={uploading}
          >
            Cancel
          </Button>
          <Button
            type="button"
            className=""
            onClick={handleUpload}
            disabled={!files.length || uploading}
          >
            {uploading ? (
              <span className="flex items-center gap-2">
                <ClipLoader size={18} color="#fff" />
              
              </span>
            ) : (
              "Confirm Upload"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
